import { Box, chakra, Flex } from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import React from "react";
import BaseDatePicker, { BaseDatePickerProps } from "./BaseDatePicker";
import RangeDatePickerRanges from "./RangeDatePickerRanges";

const CalendarsBox = chakra(Box, {
  baseStyle: {
    display: "flex",
    width: "fit-content",
    gap: 3,
  },
});

export interface RangeDatePickerProps extends Omit<BaseDatePickerProps, "onChange"> {
  onChange: (
    date: [LocalDate, LocalDate | null] | null,
    event: React.SyntheticEvent<unknown, Event> | undefined
  ) => void;
  hideDateRanges?: boolean;
}

const PropsContext = React.createContext<RangeDatePickerProps | null>(null);

// react-datepicker renders `calendarContainer` as a component, so it has to be
// declared here rather than inline: an inline arrow gets a new identity on every
// render, which makes any re-render of a parent (e.g. a Chakra Tooltip around
// the input, opening on hover) unmount and remount the whole calendar. That
// destroys the day element between mousedown and mouseup, so no click event is
// ever dispatched and dates cannot be picked at all.
function CalendarContainer({ children }: { children: React.ReactNode[] }) {
  const datePicker = React.useContext(PropsContext);

  if (datePicker === null) {
    return null;
  }

  return (
    <Flex gap={8}>
      {datePicker.hideDateRanges !== true && (
        <RangeDatePickerRanges
          endDate={datePicker.endDate}
          startDate={datePicker.startDate}
          onClear={(e) => datePicker.onChange(null, e)}
          onClick={datePicker.onChange}
        />
      )}
      <CalendarsBox>
        <>{children}</>
      </CalendarsBox>
    </Flex>
  );
}

const RangeDatePicker = (props: RangeDatePickerProps) => {
  return (
    <PropsContext.Provider value={props}>
      <BaseDatePicker
        {...props}
        calendarContainer={CalendarContainer}
        monthsShown={props.monthsShown ?? 2}
        selectsRange={true}
      />
    </PropsContext.Provider>
  );
};

export default RangeDatePicker;
